import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Container,
  Grid,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddCommentIcon from "@mui/icons-material/AddComment";
import { Link as RouterLink } from "react-router-dom";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import PageTitle from "../../components/common/PageTitle";
import ProductManagerChatPanel from "../../components/product-manager/ProductManagerChatPanel";
import { useProductManagerAgentChat } from "../../hooks/useProductManagerAgentChat";
import {
  collectPmEntityActions,
  type PmEntityActionPayload,
} from "../../utils/productManagerOutput";

const QUICK_PROMPTS = [
  "List my products and categories",
  "Create a new category for organizing APIs",
  "Show operational errors linked to my products",
  "Find FAQs similar to authentication",
];

function actionLabel(action: PmEntityActionPayload): string {
  const name = action.name ?? action.uuid ?? action.entityType;
  return `${action.action} ${action.entityType}: ${name}`;
}

const ProductManagerCatalogAssistantPage: React.FC = () => {
  const [chatMaximized, setChatMaximized] = useState(false);
  const {
    messages,
    loading,
    error,
    contextWindowExceeded,
    contextUsage,
    sessionReady,
    sessionInitializing,
    sendMessage,
    startNewChat,
  } = useProductManagerAgentChat();

  const recentActions = useMemo(
    () => collectPmEntityActions(messages).slice(-8).reverse(),
    [messages],
  );

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default" }}>
      <PageTitle
        pageTitle="Catalog Assistant"
        pageDescription="Create and edit products, categories, APIs, FAQs, and other catalog entities using natural language."
      />

      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
          <Button
            component={RouterLink}
            to="/agent-dashboard"
            startIcon={<ArrowBackIcon />}
          >
            Agents
          </Button>
          <Button
            variant="outlined"
            startIcon={<AddCommentIcon />}
            onClick={startNewChat}
            disabled={sessionInitializing || loading}
          >
            New chat
          </Button>
        </Box>
        <Grid container spacing={3}>
          <Grid item xs={12} md={chatMaximized ? 12 : 8}>
            <ProductManagerChatPanel
              messages={messages}
              onSend={sendMessage}
              loading={loading}
              error={error}
              contextWindowExceeded={contextWindowExceeded}
              contextUsage={contextUsage}
              sessionReady={sessionReady}
              sessionInitializing={sessionInitializing}
              onNewChat={startNewChat}
              quickPrompts={QUICK_PROMPTS}
              maximized={chatMaximized}
              onToggleMaximize={() => setChatMaximized((v) => !v)}
            />
          </Grid>
          {!chatMaximized && (
          <Grid item xs={12} md={4}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                <SmartToyIcon color="primary" />
                <Typography variant="subtitle1">Recent catalog actions</Typography>
              </Stack>
              {recentActions.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  Actions from this session (create, update, delete) will appear here with links
                  to open the matching hub.
                </Typography>
              ) : (
                <Stack spacing={1}>
                  {recentActions.map((action, idx) => (
                    <Box key={`${action.uuid ?? idx}-${action.action}`}>
                      <Chip
                        size="small"
                        label={action.action}
                        color={
                          action.action === "deleted"
                            ? "error"
                            : action.action === "created"
                              ? "success"
                              : "default"
                        }
                        sx={{ mr: 0.5, mb: 0.5 }}
                      />
                      <Typography variant="body2">{actionLabel(action)}</Typography>
                      {action.hubPath && (
                        <Typography
                          component={RouterLink}
                          to={action.hubPath}
                          variant="caption"
                          color="primary"
                          sx={{ display: "block", mt: 0.25 }}
                        >
                          Open in hub
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Stack>
              )}
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 2 }}>
                App KYC is not managed by this assistant — use the App KYC Hub on the dashboard.
              </Typography>
            </Paper>
          </Grid>
          )}
        </Grid>
      </Container>
    </Box>
  );
};

export default ProductManagerCatalogAssistantPage;
